import { useCallback, useEffect, useRef, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { ChevronLeft, CloudOff, Loader2, Lock, PlayCircle } from "lucide-react";
import { billsApi, billsRead, isFeatureOff } from "@/lib/billsNative";
import { CONFIRM_ERRORS } from "@/lib/billsLanguage";
import { LineCard } from "@/components/bills/LineCard";
import { ConfirmBar } from "@/components/bills/ConfirmBar";
import { DuplicatePanel } from "@/components/bills/DuplicatePanel";
import { BillHeader, BillReceipt } from "@/components/bills/BillReceipt";
import { FinancialSummary } from "@/components/bills/FinancialSummary";
import { LandedCostPanel } from "@/components/bills/LandedCostPanel";
import { VendorResolutionPanel } from "@/components/bills/VendorResolutionPanel";

const SCREENS = {
  off: { icon: Lock, title: "Bill review isn't turned on", body: "Continue with the regular review for this bill." },
  unavailable: { icon: CloudOff, title: "Bill review is temporarily unavailable", body: "Nothing was changed. Please try again shortly." },
  legacy: { icon: Lock, title: "Handled in Expense Capture (historical)", body: "This bill was handed to Expense Capture before 1OS Bills became canonical, so it can't be reviewed here." },
  notfound: { icon: Lock, title: "Bill not available", body: "This bill doesn't exist or isn't available for your outlet." },
};

const Screen = ({ kind, candidateId, children }) => {
  const s = SCREENS[kind] || {};
  const Icon = s.icon || PlayCircle;
  return (
    <section data-testid={`bill-screen-${kind}`} className="rounded-md border border-[#E4E0D8] bg-white p-6 space-y-3 text-center">
      <Icon size={28} className="mx-auto text-archival-muted" aria-hidden />
      {s.title && <h1 className="text-[18px] font-semibold text-archival-primary">{s.title}</h1>}
      {s.body && <p className="text-[14px] text-archival-secondary">{s.body}</p>}
      {children}
      {kind !== "notfound" && <Link to={`/candidates/${candidateId}`} className="inline-block min-h-[44px] pt-3 text-[14px] text-[var(--action)] underline" data-testid="bill-back-to-candidate">Back to candidate review</Link>}
    </section>
  );
};

// Informational only: compares this line against the last COMPARABLE canonical purchase (same ingredient,
// same canonical unit, confirmed bill). Never blocks Confirm, never fires on incomparable packaging.
function PriceWatch({ view, setPriceNotes }) {
  useEffect(() => {
    let live = true;
    (async () => {
      const notes = {};
      const lines = (view.lines || []).filter(
        (l) => l.resolved_ingredient_id && l.base_quantity > 0 && l.base_uom && l.total_value != null
      );
      for (const l of lines.slice(0, 12)) {
        const r = await billsRead.priceReference({
          ingredient_id: l.resolved_ingredient_id,
          vendor_id: view.vendor_id || undefined,
          exclude_bill: view.bill_id,
        });
        const ref = r.ok ? r.data?.reference : null;
        if (!ref || ref.canonical_unit !== l.base_uom || !(ref.canonical_unit_price > 0)) continue;
        const now = Number(l.total_value) / Number(l.base_quantity);
        const pct = Math.round(((now - ref.canonical_unit_price) / ref.canonical_unit_price) * 1000) / 10;
        if (Math.abs(pct) >= 5) {
          notes[l.line_id] = {
            pct,
            unit: l.base_uom,
            date: ref.date,
            billId: ref.bill_id,
            billNumber: ref.bill_number,
          };
        }
      }
      if (live) setPriceNotes(notes);
    })();
    return () => { live = false; };
  }, [view, setPriceNotes]);
  return null;
}

export default function BillReview() {
  const { candidateId } = useParams();
  const [phase, setPhase] = useState("loading");
  const [view, setView] = useState(null);
  const [busy, setBusy] = useState(null);
  const [lineErr, setLineErr] = useState({});
  const [highlight, setHighlight] = useState(null);
  const [banner, setBanner] = useState(null);
  const [dup, setDup] = useState(null);
  const [receipt, setReceipt] = useState(null);
  const [vendorErr, setVendorErr] = useState(null);
  const [vendorChange, setVendorChange] = useState(false);
  const [priceNotes, setPriceNotes] = useState({});
  const [chargeErr, setChargeErr] = useState(null);
  const [landedErr, setLandedErr] = useState(null);
  const refs = useRef({});

  const gen = useRef(0);
  const fail = (r) => (r.status === 404 ? setPhase("notfound") : r.status === 0 || r.status >= 500 ? setPhase("unavailable") : null);

  const load = useCallback(async () => {
    const g = ++gen.current;
    const live = () => g === gen.current;
    const rd = await billsApi.readiness();
    if (!live()) return;
    if (isFeatureOff(rd)) return setPhase("off");
    if (!rd.ok || !rd.data.ready) return setPhase("unavailable");
    let a = await billsApi.authority(candidateId);
    // Claimed but bill_id not written yet (start in flight elsewhere) → brief wait, never "not found".
    for (let i = 0; a.ok && a.data.authority === "BILLS_NATIVE" && !a.data.bill_id && i < 6; i++) {
      await new Promise((res) => setTimeout(res, 400));
      a = await billsApi.authority(candidateId);
    }
    if (!live()) return;
    if (!a.ok) return setPhase(a.status === 404 ? "notfound" : "unavailable");
    if (a.data.authority === "LEGACY_EC") return setPhase("legacy");
    if (a.data.authority === "UNCLAIMED") return setPhase("unclaimed");
    if (!a.data.bill_id) return setPhase("unavailable");
    const b = await billsApi.bill(a.data.bill_id);
    if (!live()) return;
    if (!b.ok) return fail(b);
    setView(b.data);
    setPhase(b.data.canonical_status === "CONFIRMED" ? "confirmed" : "review");
  }, [candidateId]);

  useEffect(() => { load(); }, [load]);

  const start = async () => {
    gen.current++; // any in-flight load is now stale
    setBusy("start");
    const r = await billsApi.start(candidateId);
    setBusy(null);
    if (r.ok) { setView(r.data); return setPhase("review"); }
    if (r.data?.error_code === "authority_legacy_ec") return setPhase("legacy");
    if (!fail(r)) setBanner(r.data?.message || "We couldn't start the review for this bill.");
  };

  const gateOf = (lid) => view?.confirm?.lines?.find((g) => g.line_id === lid);
  const pendingLines = (view?.lines || []).filter((l) => !gateOf(l.line_id)?.eligible);

  const focusLine = (lid) => {
    setHighlight(lid);
    const el = refs.current[lid];
    if (el) { el.scrollIntoView({ behavior: "smooth", block: "center" }); el.focus({ preventScroll: true }); }
  };

  const mutate = (lid, fn) => async (arg) => {
    setBusy(lid); setLineErr((e) => ({ ...e, [lid]: null })); setBanner(null);
    const r = await fn(view.bill_id, lid, arg);
    setBusy(null);
    if (r.ok) return setView(r.data);
    if (fail(r)) return;
    setLineErr((e) => ({ ...e, [lid]: r.status === 403 ? "You don't have permission to change this bill." : (CONFIRM_ERRORS[r.data?.error_code] || "That didn't save. Please check the value and try again.") }));
  };

  const confirm = async (override) => {
    setBusy("confirm"); setBanner(null);
    const r = await billsApi.confirm(view.bill_id, override ? { allow_duplicate: true, duplicate_reason: override } : undefined);
    setBusy(null);
    if (r.ok) { setDup(null); setReceipt(r.data); return setPhase("confirmed"); }
    if (fail(r)) return;
    const code = r.data?.error_code;
    if (code === "DUPLICATE_DETECTED" || code === "DUPLICATE_REASON_REQUIRED")
      return setDup({ matches: r.data.matches, reasonRequired: code === "DUPLICATE_REASON_REQUIRED" });
    setBanner(CONFIRM_ERRORS[code] || CONFIRM_ERRORS[r.data?.outcome] || r.data?.message || "This bill can't be confirmed yet.");
    const b = await billsApi.bill(view.bill_id);
    if (b.ok) {
      setView(b.data);
      const first = b.data.confirm.lines.find((g) => !g.eligible) || b.data.confirm.lines[(r.data?.line_indexes || [])[0]];
      if (first) setTimeout(() => focusLine(first.line_id), 50);
    }
  };

  const decideVendor = async (body) => {
    setBusy("vendor"); setVendorErr(null); setBanner(null);
    const r = await billsApi.decideVendor(view.bill_id, body);
    setBusy(null);
    if (r.ok) return setView(r.data);
    if (fail(r)) return;
    setVendorErr(CONFIRM_ERRORS[r.data?.error_code] || "That didn't save. Please try again.");
  };

  const classifyCharge = async (chargeId, kind) => {
    setBusy("charge"); setChargeErr(null);
    const r = await billsApi.classifyCharge(view.bill_id, chargeId, kind);
    setBusy(null);
    if (r.ok) return setView(r.data);
    if (fail(r)) return;
    setChargeErr(r.data?.message || CONFIRM_ERRORS[r.data?.error_code] || "That decision didn't save. Please try again.");
  };

  const overrideLanded = async (body) => {
    setBusy("landed"); setLandedErr(null);
    const r = await billsApi.landedCostOverride(view.bill_id, body);
    setBusy(null);
    if (r.ok) return setView(r.data);
    if (fail(r)) return;
    setLandedErr(r.data?.message || "That decision didn't save. Please try again.");
  };

  const canEdit = !!view?.can_confirm && phase === "review";  return (
    <div className="min-h-screen bg-archival-base" data-testid="bill-review-page">
      <div className="max-w-[640px] mx-auto px-4 pt-4 pb-32 space-y-4">
        <div className="flex items-center justify-between">
          <Link to="/bills" className="inline-flex items-center gap-1 min-h-[44px] text-[14px] text-archival-secondary" data-testid="bill-back-to-bills">
            <ChevronLeft size={16} aria-hidden />Bills
          </Link>
          <Link to={`/candidates/${candidateId}`} className="inline-flex items-center min-h-[44px] text-[13px] text-archival-secondary underline underline-offset-2" data-testid="bill-back-link">
            Captured bill
          </Link>
        </div>
        {phase === "loading" && <p className="flex items-center gap-2 text-archival-secondary" data-testid="bill-loading"><Loader2 size={16} className="animate-spin" />Loading bill…</p>}
        {["off", "unavailable", "legacy", "notfound"].includes(phase) && <Screen kind={phase} candidateId={candidateId} />}
        {phase === "unclaimed" && (
          <Screen kind="unclaimed" candidateId={candidateId}>
            <h1 className="text-[18px] font-semibold text-archival-primary">Review this bill here</h1>
            <p className="text-[14px] text-archival-secondary">Check each item's ingredient and quantity, then confirm the bill.</p>
            {banner && <p role="alert" className="text-[13px] text-red-700" data-testid="bill-banner">{banner}</p>}
            <button type="button" onClick={start} disabled={busy === "start"} data-testid="start-bills-review-btn"
              className="h-12 px-6 rounded-md text-white text-[15px] font-semibold disabled:opacity-60" style={{ backgroundColor: "var(--action)" }}>
              {busy === "start" ? <Loader2 size={16} className="animate-spin" /> : "Start Bills review"}
            </button>
          </Screen>
        )}
        {phase === "confirmed" && <BillReceipt bill={view?.bill} billId={receipt?.bill_id || view?.bill_id} items={view?.lines?.length} />}
        {phase === "review" && view && (
          <>
            <BillHeader bill={view.bill} canEdit={canEdit} onChangeVendor={() => setVendorChange(true)} />
            <FinancialSummary fin={view.financials} canEdit={canEdit} busy={busy === "charge"} error={chargeErr}
              onClassify={classifyCharge} />
            <LandedCostPanel landed={view.landed_cost} currency={view.bill?.currency} canEdit={canEdit}
              busy={busy === "landed"} error={landedErr} onOverride={overrideLanded} />
            {!view.can_confirm && <p className="text-[13px] rounded-md bg-archival-soft px-3 py-2" data-testid="bill-readonly-note">View only — you can't change or confirm bills.</p>}
            {banner && <p role="alert" className="text-[14px] rounded-md border border-amber-300 bg-amber-50 px-3 py-2 text-amber-900" data-testid="bill-banner">{banner}</p>}
            {view.bill?.vendor?.state === "VENDOR_IDENTITY_RESOLUTION_REQUIRED" && (
              <VendorResolutionPanel identity={view.bill.vendor} canEdit={canEdit} busy={busy === "vendor"} error={vendorErr} onDecide={decideVendor} />
            )}
            {vendorChange && view.bill?.vendor?.change_options && (
              <VendorResolutionPanel identity={view.bill.vendor.change_options} canEdit={canEdit} busy={busy === "vendor"} error={vendorErr}
                onDecide={async (b) => { await decideVendor(b); setVendorChange(false); }} />
            )}
            {dup && <DuplicatePanel dup={dup} canOverride={view.can_override_duplicate} busy={busy === "confirm"} onOverride={(why) => confirm(why)} onDismiss={() => setDup(null)} />}
            <div className="space-y-3" data-testid="line-list">
              <PriceWatch view={view} setPriceNotes={setPriceNotes} />
              {view.lines.map((l) => (
                <LineCard key={l.line_id} ref={(el) => (refs.current[l.line_id] = el)} line={l} gate={gateOf(l.line_id)}
                  canEdit={canEdit} busy={busy === l.line_id} error={lineErr[l.line_id]} highlight={highlight === l.line_id}
                  priceNote={priceNotes[l.line_id]}
                  onIngredient={mutate(l.line_id, billsApi.setIngredient)} onConversion={mutate(l.line_id, billsApi.setConversion)}
                  onSource={mutate(l.line_id, billsApi.setSource)} />
              ))}
            </div>
            <ConfirmBar pending={pendingLines.length} canConfirm={view.can_confirm} confirming={busy === "confirm"}
              onReview={() => pendingLines[0] && focusLine(pendingLines[0].line_id)} onConfirm={() => confirm()}
              notice={dup ? "Decide on the duplicate above" : view.financials?.needs_human_review ? "Classify the printed charge above" : view.bill?.vendor?.state === "VENDOR_IDENTITY_RESOLUTION_REQUIRED" ? "Choose the vendor above" : null} />
          </>
        )}
      </div>
    </div>
  );
}
